import { Alert, Badge, Code, Group, Loader, Stack, Text } from "@mantine/core";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import type { HistoryEntry, OverrideKind } from "./api";
import { useRoleHistory } from "./useRoleHistory";
import { agentRoleErrorText } from "./refusalText";

// Общая история override'ов роли (промпт + модель в одном запросе, см.
// `useRoleHistory`) — фильтрует общий ответ по `kind` и показывает не
// более HISTORY_SHOWN последних записей своей секции, свежие первыми
// (порядок задаёт backend). Переиспользуется и для промпта (RoleEditor),
// и для модели (RoleModelEditor) — различаются только префикс
// i18n-ключей действия (`prefix`) и форма значения (усечённый Code-блок
// с полным текстом промпта в title-tooltip vs inline Code с именем
// модели).

const HISTORY_SHOWN = 10;

function actionColor(action: HistoryEntry["action"]): string {
  return action === "delete" ? "gray" : "blue";
}

function actorLabel(entry: HistoryEntry, t: TFunction): string {
  if (entry.edited_by === null) {
    return t("agentRoles.history.actorSystem");
  }
  return entry.edited_by;
}

function HistoryValue({
  kind,
  value,
}: {
  readonly kind: OverrideKind;
  readonly value: string;
}): ReactElement {
  if (kind === "prompt") {
    return (
      <Code block title={value}>
        <Text component="span" lineClamp={3}>
          {value}
        </Text>
      </Code>
    );
  }
  return <Code>{value}</Code>;
}

function HistoryRow({
  entry,
  kind,
  prefix,
  t,
}: {
  readonly entry: HistoryEntry;
  readonly kind: OverrideKind;
  readonly prefix: string;
  readonly t: TFunction;
}): ReactElement {
  return (
    <Stack gap={4}>
      <Group gap="xs">
        <Badge color={actionColor(entry.action)} variant="light">
          {t(`${prefix}.action.${entry.action}`)}
        </Badge>
        <Badge variant="outline">
          {t(`agentRoles.source.${entry.source}`)}
        </Badge>
        <Text size="sm" c="dimmed">
          {t("agentRoles.history.by", { actor: actorLabel(entry, t) })}
        </Text>
        <Text size="sm" c="dimmed">
          {entry.created_at}
        </Text>
      </Group>
      <HistoryValue kind={kind} value={entry.value} />
    </Stack>
  );
}

export function OverrideHistory({
  roleName,
  kind,
}: {
  readonly roleName: string;
  readonly kind: OverrideKind;
}): ReactElement {
  const { t } = useTranslation();
  const prefix =
    kind === "prompt" ? "agentRoles.history" : "agentRoles.model.history";
  const history = useRoleHistory(roleName);

  if (history.isLoading) {
    return (
      <Group gap="xs">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          {t(`${prefix}.loading`)}
        </Text>
      </Group>
    );
  }

  if (history.isError) {
    return (
      <Alert color="red" title={t(`${prefix}.loadError`)}>
        {agentRoleErrorText(history.error, t)}
      </Alert>
    );
  }

  const entries = (history.data ?? [])
    .filter((entry) => entry.kind === kind)
    .slice(0, HISTORY_SHOWN);
  if (entries.length === 0) {
    return <Text c="dimmed">{t(`${prefix}.empty`)}</Text>;
  }

  return (
    <Stack gap="sm">
      {entries.map((entry, index) => (
        <HistoryRow
          key={`${entry.created_at}-${entry.action}-${index}`}
          entry={entry}
          kind={kind}
          prefix={prefix}
          t={t}
        />
      ))}
    </Stack>
  );
}
